import { getCustomisationOptions } from 'js/crossword/getCustomisationOptions';
import OS from 'framework/utils/OS';
import { replaceAll } from 'framework/utils/misc-utils';

function applyValuesFromViewSettings(css: string): string {
  let currentCss = css;
  const customisationOptions = getCustomisationOptions();

  Object.entries({
    '%FONTFAMILY%': customisationOptions.fontFamily,
    '%selectedSquareHighlightColor%': customisationOptions.selectedSquareHighlightColor,
    '%mainUIColor%': customisationOptions.mainUIColor,
    '%secondClueColor%': customisationOptions.secondClueColor,
    '%scrollBarHeadColor%': customisationOptions.scrollBarHeadColor,
    '%scrollBarBackgroundColor%': customisationOptions.scrollBarBackgroundColor,
    '%buttonBackgroundColor%': customisationOptions.buttonBackgroundColor,
    '%cellRevealedFontColor%': customisationOptions.cellRevealedFontColor,
    '%cellCorrectFontColor%': customisationOptions.cellCorrectFontColor,
    '%buttonTextColor%': customisationOptions.buttonTextColor,
  }).forEach(([toReplace, replacer]) => {
    currentCss = replaceAll(currentCss, toReplace, replacer!);
  });

  return currentCss;
}

export function setCss(): void {
  let existing = document.getElementById('crosswordStyle');
  if (!existing) {
    existing = document.createElement('style');
    document.head.appendChild(existing);
  }
  existing.innerText = applyValuesFromViewSettings(css);

  setCustomProperties();
}

function setCustomProperties() {
  const customisationOptions = getCustomisationOptions();
  const bodyStyles = document.body.style;

  Object.keys(customisationOptions).forEach(key => {
    bodyStyles.setProperty(`--${key}`, customisationOptions[key]);
    
    if (key === "secondClueColor") {
      let color = customisationOptions[key]!;
      if (color[0] === "#") color = hexToRgba(color, 0.1); 
      bodyStyles.setProperty(`--secondClueColor10percent`, color);
    }
  });
}

function hexToRgba(hex: string, a = 1): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  
  if (!result) return hex;

  const color = {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };

  return `rgba(${color.r}, ${color.g}, ${color.b}, ${a})`;
}

const css = `
#mobileTextInput {
  position: fixed;
  left: 0px;
  top: 300px;
  opacity: 0;
}

* {
  font-family: %FONTFAMILY%;
}

#gameScene {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: min(46px, 8vh) calc(100vh - min(46px, 8vh) - 28px);
}

#printPage {
  display: none;
  width: 100%;
  height: 100%;
  position: relative;
}

@media print {
  div {
    break-inside: avoid;
  }
  
  #printPage {
    display: block;
  }

  #gameScene {
    display: none;
  }
}

#gameScene__topBar {
}

#gameScene__mainArea {
  width: 100vw;
}

#gameScene__acrossColumn {
  margin: 5px;
  grid-area: acrossClues;
}

#gameScene__downColumn {
  margin: 5px;
  grid-area: downClues;
}

#gameScene__boardColumn {
  display: flex;
  flex-direction: column;
  margin: 5px;
  grid-area: board;
  overflow: hidden;
}

#gameScene__boardContainer {
  overflow: hidden;
  margin-top: 5px;
  margin-bottom: 5px;
  width: 100%;
  height: 100%;
}

.gameScene__mainArea--oneRight {
  display: grid;
  grid-template-columns: 70% 30%;
  grid-template-rows: 50% 50%;
  grid-template-areas:
    "board acrossClues"
    "board downClues";
}

.gameScene__mainArea--oneLeft {
  display: grid;
  grid-template-columns: 30% 70%;
  grid-template-rows: 50% 50%;
  grid-template-areas:
    "acrossClues board"
    "downClues board";
}

.gameScene__mainArea--twoRight {
  display: grid;
  grid-template-columns: 60% 20% 20%;
  grid-template-rows: 100%;
  grid-template-areas:
    "board acrossClues downClues";
}

.gameScene__mainArea--twoLeft {
  display: grid;
  grid-template-columns: 20% 20% 60%;
  grid-template-rows: 100%;
  grid-template-areas:
    "acrossClues downClues board";
}

.gameScene__mainArea--mobile {
  display: grid;
  grid-template-columns: 100%;
  grid-template-rows: 1fr auto auto;
  grid-template-areas:
    "board"
    "acrossClues"
    "downClues";
}

.board {
  box-sizing: border-box;
  display: grid;
  font-size: min(1em, 2.5vw, 2.4vh);
  touch-action: pan-y;
  margin: auto;
}

.board--print {
  width: 100%;
  height: 100%;
  color-adjust: exact;
  -webkit-print-color-adjust: exact;
}

.board--transformAnimated {
  transition: transform 50ms;
}

.board--copyright {
  text-align: center;
  font-size: 0.73em;
  margin-top: 0.4em;
}

.currentClueBar {
  font-family: %FONTFAMILY%;
  box-sizing: border-box;
  height: 3em;
  width: 100%;
  background-color: %mainUIColor%;
  border-radius: 5px;
  align-items: center;
  justify-content: space-between;
}

.currentClueBar--mobile {
  font-size: 1.1em;
  height: 3.2em;
}

.currentClueBar__arrowWrapper {
  width: 2.5em;
  height: 100%;
  cursor: pointer;
  position: relative;
}

.currentClueBar__arrow {
  height: 1.1em;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.currentClueBar__arrow--rotated {
  transform: translate(-50%, -50%) rotate(180deg);
}

.currentClueBar__textWrapper {
  width: 100%;
  height: 100%;
  cursor: pointer;
  display: flex;
  align-items: center;
}

.currentClueBar__text {
  width: 100%;
}

.currentClueBar__wrapper {
  display: flex;
  height: 100%;
  align-items: center;
}

.currentClueBar__wrapper--hidden {
  opacity: 0;
}

.currentClueBar__text--hidden {
  color: rgba(0,0,0,0);
}

.clueColumn {
  font-family: %FONTFAMILY%;
  display: flex;
  flex-direction: column;
  background-color: #f1f1f1;
  border-radius: 5px;
  font-size: min(2.4vw, 14px);
}

@media only screen and (max-width: 600px) {
  .clueColumn {
    font-size: ${ OS.desktop ? '12px' : '1em'};
  }
}

.clueColumn__footer {
  display: flex;
  padding: 0.25em 0.25em 0.25em 0.5em;
  cursor: pointer;
  border-radius: 0px 0px 5px 5px;
  flex-shrink: 0;
}

.clueColumn__headerTextWrapper {
  min-width: 50%;
}

.clueColumn__headerTextWrapper--invisible {
  color: rgba(0,0,0,0);
  min-width: 50%;
}

.clueColumn__arrowBackground {
  position: absolute;
  width: 1.5em;
  height: 1em;
  background-color: #c4c4c4;
  border-radius: 5px;
  transform: translate(-50%, 10%);
  opacity: 0;
  transition: opacity 200ms;
}

.clueColumn__top:hover .clueColumn__arrowBackground {
  opacity: 1;
}

.clueColumn__footer:hover .clueColumn__arrowBackground {
  opacity: 1;
}

.clueColumn__bottomArrow {
  width: 1em;
  transform: translate(-50%);
}

.clueColumn__topArrow {
  width: 1em;
  transform: translate(-50%) rotate(180deg);
}

.clueColumn__top {
  display: flex;
  padding: 0.5em;
  position: relative;
  cursor: pointer;
  border-radius: 5px 5px 0px 0px;
  flex-shrink: 0;
}

.clueColumn__header {
  margin: 0 1em 0 0.5em;
  font-weight: bold;
  text-transform: uppercase;
}

.clueColumn__header--mobile {
  font-size: 1.05em;
}

.clueColumn--clickAnimation {
  animation: clueUpDownAnimation 100ms 2 alternate;
}

@-webkit-keyframes clueUpDownAnimation {
  from { background-color: rgba(0, 0, 0, 0) }
  to { background-color: rgba(0, 0, 0, 0.1) }
}

.clueColumn__topArrow--clickAnimation {
  animation: clueTopArrowAnimation 100ms 2 alternate;
}

@-webkit-keyframes clueTopArrowAnimation {
  from { transform: translate(-50%, 0%) rotate(180deg); }
  to { transform: translate(-50%, -20%) rotate(180deg); }
}

.clueColumn__bottomArrow--clickAnimation {
  animation: clueBottomArrowAnimation 100ms 2 alternate;
}

@-webkit-keyframes clueBottomArrowAnimation {
  from { transform: translate(-50%, 0%); }
  to { transform: translate(-50%, 20%); }
}

.currentClueBar__arrowWrapper--clickAnimation {
  animation: currentClueBarArrowWrapperAnimation 200ms;
}

@-webkit-keyframes currentClueBarArrowWrapperAnimation {
  from { background-color: rgba(255,255,255,0.7); }
  to { background-color: rgba(255,255,255,0); }
}

.clueColumn__scrollView {
  overflow: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: %scrollBarHeadColor% %scrollBarBackgroundColor%;
}

.clueColumn__scrollView::-webkit-scrollbar {
  width: 4px;
}

.clueColumn__scrollView::-webkit-scrollbar-track {
  background: %scrollBarBackgroundColor%;
  border-radius: 5px;
}

.clueColumn__scrollView::-webkit-scrollbar-thumb {
  background-color: %scrollBarHeadColor%;
  border-radius: 5px;
}

button {
  outline: none;
}

.popup {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background-color: #ffffff;
  border-radius: 5px;
  font-family: %FONTFAMILY%;
  font-size: ${OS.desktop ? '15.5px' : '1.3em'};
  -webkit-box-shadow: 0px 7px 18px 0px rgba(0,0,0,0.75);
  -moz-box-shadow: 0px 7px 18px 0px rgba(0,0,0,0.75);
  box-shadow: 0px 7px 18px 0px rgba(0,0,0,0.75);
}

.popup--mobile {
  top: 0%;
  transform: translate(-50%, 0%);
  width: 100%;
}

.popup--notComplete {
  width: 30em;
}

.clearPuzzlePopup {
  width: 26em;
}

.clearPuzzlePopup__message {
  margin-bottom: 2em;
  text-align: center;
}

.settingsPopup__scrollView {
  overflow: auto;
  overflow-x: hidden;
  scrollbar-width: thin;
  scrollbar-color: %scrollBarHeadColor% %scrollBarBackgroundColor%;
  font-size: 0.85em;
  margin: 0 1.8em;
}

.settingsPopup__scrollView::-webkit-scrollbar {
  width: 4px;
}

.settingsPopup__scrollView::-webkit-scrollbar-track {
  background: %scrollBarBackgroundColor%;
  border-radius: 5px;
}

.settingsPopup__scrollView::-webkit-scrollbar-thumb {
  background-color: %scrollBarHeadColor%;
  border-radius: 5px;
}

.popup--print {
  width: 28em;
}

.popup--revealAdPopup {
  width: 30em;
}

.popup--completePopup {
  width: 30em;
}

.adPopup {
  min-width: 400px;
  max-width: 500px;
  padding: 3em;
}

.adPopup--content-wrapper {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 0.3em;
}

.adPopup--content-wrapper div {
  margin: 0.5em 0;
  text-align: center;
  position: relative;
}

.adPopup--icon {
  width: 6em;
  margin-bottom: 1em;
}

.adPopup__icon--reveal {
  width: 5.1em;
}

.printPopup__checkIcon {
  position: absolute;
  height: 4em;
  left: 2.5em;
  top: 2.1em;
}

.printPopup__typeInputContainer {
  display: flex;
  justify-content: space-evenly;
  margin: 0 2em 2em 2em;
}

@media (max-width: 375px) {
  .printPopup__typeInputContainer {
    margin: 0 0em 2em 0em;
  }  
}

.settingsPopupBody__buttonWrapper {
  padding-top: 4em;
}

.pauseMessageContainer {
  font-size: 1em;
  padding: 2em;
  margin-bottom: 0.5em;
  display: flex;
  justify-content: center;
  align-items: center;
}

.pauseMessageContainer__icon {
  height: 4em;
}

.pauseMessageContainer__textWrapper {
  margin-left: 1em;
}

.popupContent {
  padding: 1.5em;
}

@media (max-width: 375px) {
  .popupContent {
    padding: 1.5em 0.7em;
  }  
}

.headerIcon {
  display: inline-block;
  vertical-align: middle;
  height: 1.5em;
}

.headerText {
  vertical-align: middle;
  font-weight: bold;
}

.popupButton {
  background-color: %buttonBackgroundColor%;
  color: %buttonTextColor%;
  border: 0;
  border-radius: 1em;
  padding: 0.5em 1.5em;
  font-size: 0.882em;
  cursor: pointer;
  transition: background-color 0.3s;
  min-width: 6em;
}

.popupButton:hover {
  background-color: %scrollBarHeadColor%;
}

.popupButton--reversed {
  color: %secondClueColor%;
  background-color: white;
  border: 1px solid %secondClueColor%;
  transition: background-color 0.3s, color 0.3s;
}

.popupButton--reversed:hover {
  color: white;
}

.center {
  display: block;
  margin: auto;
}

.calendarMonthPicker {
  width: 100%;
  box-sizing: border-box;
  display: flex;
  justify-content: space-between;
  padding: 0.5em;
  padding-top: 0;
  align-items: center;
}

@media (max-width: 375px) {
  .calendarMonthPicker {
    padding: 0 0.5em;
  }
}

.calendarMonthPicker__labelContainer {
  font-size: 2em;
  text-align: center;
}

.calendarMonthPicker__labelContainer--mobile {
  font-size: 1.3em;
}

.calendarMonthPicker__monthLabel {
  font-weight: bold;
}

.calendarMonthPicker__arrowWrapper {
  width: 2.5em;
  height: 2.5em;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  cursor: pointer;
}

@media (hover: hover) {
  .calendarMonthPicker__arrowWrapper:hover {
    background-color: #F1F1F1;
  }
}

.calendarMonthPicker__arrow {
  cursor: pointer;
  max-height: 1.55em;
}

.calendarMonthPicker__arrow--left {
  transform: translate(-15%, 0%) scaleX(-1);
}

.calendarMonthPicker__arrow--right {
  transform: translate(15%, 0%);
}

.calendarProgressPanel {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin: 1em 0;
}

@media (max-width: 375px) {
  .calendarProgressPanel {
    margin: 0.4em;
  }
}

.calendarProgressPanel__item {
  font-size: 0.7em;
  flex-grow: 2;
  max-width: 33%;
  text-align: center;
}

.calendarProgressPanel__itemMainValue {
  font-size: 1.5em;
  margin: 0.2em;
  color: gray;
  display: flex;
  justify-content: center;
  align-items: center;
}

.calendarProgressPanel__starIcon {
  height: 0.9em;
  margin: 0 0.2em 0.15em 0;
}

.calendarProgressPanel__separator {
  width: 0.12em;
  height: 3em;
  background-color: #C4C4C4;
}

.calendarProgressPanel__label--mobile {
  font-size: 0.8em;
}

button {
  -webkit-tap-highlight-color: transparent !important;
}

button: active {
  -webkit-tap-highlight-color: transparent !important;
  background-color: #f1f1f1 !important;
}

`;
